import { useEffect, useEffectEvent, useRef } from 'react';
import type { FleetMapStop, FleetMapTrail, FleetMapVehicle } from '@bonbon/types';
import { cameraFor, trailPath } from './markers';
import { useDecidedMap } from './useDecidedMap';

const VIEWPORT_PADDING = 64;

/** Moves the camera to show the points, never closer than a sensible zoom. */
function showPoints(
  map: google.maps.Map,
  points: readonly google.maps.LatLngLiteral[],
): void {
  const target = cameraFor(points);
  if (target === null) return;
  if (target.kind === 'point') {
    map.moveCamera({ center: target.center, zoom: target.zoom });
  } else {
    map.fitBounds(target.bounds, VIEWPORT_PADDING);
  }
}

interface MapViewportProps {
  vehicles: readonly FleetMapVehicle[];
  stops: readonly FleetMapStop[];
  trail: FleetMapTrail | null;
  selectedId: string | null;
  selectedStopId: string | null;
}

/**
 * Moves the camera only on events the user would expect it to follow: the
 * first positions (show the fleet), stops arriving where there were none (the
 * day's stops, whether first, or after switching day: show them with the
 * vans), a selection (bring the van or stop into view) and a trail arriving
 * for the selected van (show its day). Position updates alone never move it,
 * so a dispatcher's own panning is left where they put it. A selected van,
 * stop or trail has the camera: stops arriving then do not take it away.
 *
 * Google fixes a map's colour scheme when it is created, so a theme change
 * builds a new map instance at the default camera. The camera the old one had
 * is put back on the new one, and a selection or trail that was already shown
 * is not shown again.
 *
 * Nothing moves the camera before Google has decided how to draw the map
 * (`useDecidedMap`): a move made earlier is dropped, which on a browser that
 * falls back to a raster map left the fleet out of view for good.
 */
export function MapViewport({
  vehicles,
  stops,
  trail,
  selectedId,
  selectedStopId,
}: MapViewportProps): null {
  const map = useDecidedMap();
  const fittedFleet = useRef(false);
  const framedVehicles = useRef(false);
  const framedStops = useRef<ReadonlySet<string> | null>(null);
  const savedCamera = useRef<google.maps.CameraOptions | null>(null);
  const shownSelection = useRef<string | null>(null);
  const shownStopSelection = useRef<string | null>(null);
  const shownTrail = useRef<string | null>(null);
  const trailVehicleId = trail?.telematicsVehicleId ?? null;

  const fitFleet = useEffectEvent((target: google.maps.Map): void => {
    showPoints(target, [
      ...vehicles.map(({ lat, lng }) => ({ lat, lng })),
      ...stops.map(({ lat, lng }) => ({ lat, lng })),
    ]);
  });

  // Judged on what is selected now, not on what the camera last moved for. A
  // stop selection holds the camera only while that stop is in the list: when
  // a day is switched, the new stops can arrive before the host has cleared
  // the old selection (two messages, which React may render apart), and a
  // selection of a stop that is gone has nothing to hold.
  const cameraTaken = useEffectEvent(
    (): boolean =>
      selectedId !== null ||
      (selectedStopId !== null && stops.some(({ id }) => id === selectedStopId)) ||
      trail !== null,
  );

  const showSelected = useEffectEvent((target: google.maps.Map): void => {
    const van = vehicles.find((v) => v.telematicsVehicleId === selectedId);
    if (van !== undefined) target.panTo({ lat: van.lat, lng: van.lng });
  });

  const showSelectedStop = useEffectEvent((target: google.maps.Map): void => {
    const stop = stops.find((s) => s.id === selectedStopId);
    if (stop !== undefined) target.panTo({ lat: stop.lat, lng: stop.lng });
  });

  const showTrail = useEffectEvent((target: google.maps.Map): void => {
    const van = vehicles.find((v) => v.telematicsVehicleId === trailVehicleId);
    showPoints(target, [
      ...trailPath(trail?.points ?? []),
      ...(van === undefined ? [] : [{ lat: van.lat, lng: van.lng }]),
    ]);
  });

  // A new instance starts at the default camera: put back where the last was.
  // Read in the cleanup, while the old instance still holds it.
  useEffect(() => {
    if (map === null) return undefined;
    if (savedCamera.current !== null) map.moveCamera(savedCamera.current);
    return () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      if (fittedFleet.current && center !== undefined && zoom !== undefined) {
        savedCamera.current = { center: center.toJSON(), zoom };
      }
    };
  }, [map]);

  // The vans and the day's stops come from separate requests, in either order:
  // the first positions fit the fleet, and stops that arrive where there were
  // none, or that are all different from the last ones (another day), fit it
  // again so they are not left off screen (the vans sit at the depot early in
  // the day, the stops across town), unless a selection or a trail already has
  // the camera. A refreshed list that still has some of the same stops does
  // not move it.
  const hasVehicles = vehicles.length > 0;
  const hasStops = stops.length > 0;
  useEffect(() => {
    if (!hasStops) framedStops.current = null;
    if (map === null) return;
    const before = framedStops.current;
    const newStops =
      hasStops && (before === null || !stops.some(({ id }) => before.has(id)));
    if (hasStops) framedStops.current = new Set(stops.map(({ id }) => id));
    if (!(hasVehicles && !framedVehicles.current) && !newStops) return;
    const refit = fittedFleet.current;
    framedVehicles.current = framedVehicles.current || hasVehicles;
    if (refit && cameraTaken()) return;
    fittedFleet.current = true;
    fitFleet(map);
  }, [map, hasVehicles, hasStops, stops]);

  useEffect(() => {
    if (map === null || shownSelection.current === selectedId) return;
    shownSelection.current = selectedId;
    if (selectedId !== null) showSelected(map);
  }, [map, selectedId]);

  useEffect(() => {
    if (map === null || shownStopSelection.current === selectedStopId) return;
    shownStopSelection.current = selectedStopId;
    if (selectedStopId !== null) showSelectedStop(map);
  }, [map, selectedStopId]);

  useEffect(() => {
    if (map === null || shownTrail.current === trailVehicleId) return;
    shownTrail.current = trailVehicleId;
    if (trailVehicleId !== null) showTrail(map);
  }, [map, trailVehicleId]);

  return null;
}
